/**
 * Block-instance resolution shared by the SVG renderer and the two
 * Insert-dialog bounds consumers.
 *
 * ── Why this module exists (codex PR #472 round-3, finding 1) ─────────────
 * The Insert-dialog preview-bounds path historically RE-DERIVED, by hand,
 * the transform `scene/blockRender.tsx::renderBlockChildEntity` applies to a
 * nested `Block` child. That hand copy kept drifting from the real renderer
 * (three review rounds of the same defect class: it matched an OUTDATED
 * `translate·scale·rotate` string after the renderer had already moved to
 * `composeBlockMatrix`; it re-evaluated dynamic state by block NAME instead
 * of per-instance; it dropped unresolved-marker insertion points; it framed
 * MTEXT / bulged-LWPOLYLINE leaves around geometry the SVG renderer never
 * draws). The systemic fix is to STOP re-imitating the renderer and instead
 * share the SAME recursion primitive the renderer itself uses:
 *
 *   - {@link resolveNestedBlockChild} is the ONE place that classifies a
 *     nested `Block` child (depth-cap / cycle / missing / resolved), builds
 *     its `composeBlockMatrix` transform (T·R·S·T(−basePoint), the nested
 *     def's OWN base point), and evaluates its per-INSTANCE dynamic state via
 *     `evaluateBlock`. `renderBlockChildEntity` (DOM) AND
 *     {@link computeRenderedPreviewBounds} (AABB) both call it, so their
 *     transform + evaluation can never diverge again — by construction, not
 *     by a periodically-resynced copy.
 *
 * `computeRenderedPreviewBounds` walks the identical recursion the renderer
 * walks but accumulates an axis-aligned box instead of emitting SVG, using
 * renderer-matched LEAF bounds ({@link previewLeafLocalBounds}) for the few
 * kinds whose full-fidelity cad-core bounds differ from what the (limited)
 * SVG preview renderer draws.
 *
 * {@link specializeBlockInstance} serves the OTHER consumer — the P7
 * overlap-warning footprint (full AutoCAD-parity geometry via cad-core, all
 * kinds, MINSERT-tiled). cad-core resolves a block's geometry by NAME, which
 * cannot represent two nested INSERTs of the SAME dynamic block carrying
 * DIFFERENT visibility / parameter state (codex PR #472 round-3 finding 2).
 * `specializeBlockInstance` rewrites the tree so every nested INSERT
 * reference points at a UNIQUELY-named synthetic definition keyed by its
 * recursion PATH, pre-evaluated with that instance's own state (finding 5),
 * and materialises an insertion-point marker for every unresolved
 * (missing / cyclic) reference (finding 3) — then hands the rewritten,
 * per-instance tree to cad-core unchanged. A MLEADER child carrying BLOCK
 * content (contentType 1) references ANOTHER block by name that is NOT a
 * nested INSERT child, so its referenced definition is specialized INTO the
 * table too — otherwise cad-core's name-keyed `multileaderBlockContentBounds`
 * cannot find it and drops the content geometry (codex PR #472 round-4
 * finding 3).
 */

import {
  boundsFromPoints,
  type BlockDefinition,
  type DimensionStyle,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import { getEntityBounds } from "@dxf-ai-editor/cad-core";
import { evaluateBlock } from "@dxf-ai-editor/block-evaluator";
import { composeBlockMatrix, type BlockMatrix } from "./blockMatrix";
import { applyDimensionStyleOverrides } from "./dimensionHelpers";

type BlockChild = BlockDefinition["entities"][number];
type BlockInsertChild = Extract<BlockChild, { type: "Block" }>;

/** Axis-aligned box in a single coordinate frame. */
export interface Aabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/* ── Shared recursion primitive (renderer + preview bounds) ──────────────── */

/**
 * Result of resolving one nested `Block` child, exactly as
 * `renderBlockChildEntity` classifies it. The three marker kinds each cause
 * the renderer to draw a `<text>` glyph AT `child.insertionPoint`
 * (`(...name)` / `(↺name)` / `?name`); `resolved` carries everything the
 * renderer needs to draw the nested `<g transform>` subtree.
 */
export type NestedBlockResolution =
  | { readonly kind: "depth-cap" }
  | { readonly kind: "cycle" }
  | { readonly kind: "missing" }
  | {
      readonly kind: "resolved";
      readonly def: BlockDefinition;
      /** `composeBlockMatrix` transform for the nested `<g>` (== the SVG the
       *  renderer emits). */
      readonly matrix: BlockMatrix;
      /** Per-instance evaluated child entities (visibility-filtered, dynamic
       *  actions applied) — the SAME set the renderer recurses into. */
      readonly evaluatedEntities: readonly BlockChild[];
      /** Ancestor-name cycle-guard set for the next recursion level. */
      readonly nextVisited: Set<string>;
    };

/**
 * The single source of truth for how `renderBlockChildEntity` resolves a
 * nested `Block` child — the check ORDER (depth cap → cycle → missing →
 * resolved), the `composeBlockMatrix` transform (the nested def's OWN base
 * point, `R·S` order), and the per-instance `evaluateBlock` call all live
 * here so the renderer and the preview-bounds walker share them verbatim.
 */
export function resolveNestedBlockChild(
  child: BlockInsertChild,
  blockDefinitions: Record<string, BlockDefinition>,
  remainingDepth: number,
  visitedBlocks: ReadonlySet<string> | undefined,
): NestedBlockResolution {
  if (remainingDepth <= 0) return { kind: "depth-cap" };
  if (visitedBlocks && visitedBlocks.has(child.blockName)) {
    return { kind: "cycle" };
  }
  const def = blockDefinitions[child.blockName];
  if (!def) return { kind: "missing" };

  const matrix = composeBlockMatrix({
    translate: child.insertionPoint,
    rotateDeg: child.rotation,
    scaleX: child.scaleX,
    scaleY: child.scaleY,
    basePoint: def.basePoint,
  });

  const nextVisited = new Set(visitedBlocks ?? []);
  nextVisited.add(child.blockName);

  // parentChain is the ancestor set BEFORE this child's name is added —
  // identical to `renderBlockChildEntity`'s `Array.from(visitedBlocks)`.
  const parentChain = Array.from(visitedBlocks ?? []);
  const evaluation = evaluateBlock({
    blockDef: def,
    instance: {
      customProperties: child.customProperties ?? {},
      ...(child.visibilityState !== undefined
        ? { visibilityState: child.visibilityState }
        : {}),
    },
    blockDefinitions,
    parentChain,
  });

  return {
    kind: "resolved",
    def,
    matrix,
    evaluatedEntities:
      evaluation.entities as unknown as readonly BlockChild[],
    nextVisited,
  };
}

/* ── Insert-dialog SVG preview viewBox bounds ────────────────────────────── */

/** Depth cap matching `renderBlockInstance`'s default `maxDepth`. */
const PREVIEW_MAX_DEPTH = 8;

/**
 * Child kinds `renderBlockChildEntity` actually draws — the preview walker
 * frames ONLY these (every other `BlockChildEntity` kind — Ellipse / Spline /
 * Hatch / Dimension / Multileader / Revision Cloud — the SVG renderer emits
 * `null` for, so it must contribute nothing to the viewBox or the visible
 * shape shrinks into a corner of a mostly-empty frame).
 */
const PREVIEW_RENDERABLE_KINDS = new Set<BlockChild["type"]>([
  "Line",
  "Circle",
  "Arc",
  "LWPOLYLINE",
  "Text",
  "MText",
  "Attdef",
  "Block",
]);

/**
 * Degenerate single-point AABB. Exported so `insertionOverlap.ts` can reuse
 * the SAME primitive for its own insertion-point-only Text override (codex PR
 * #472 round-6 finding — see that module's `resolveEntityBoundsForOverlap`
 * doc) rather than re-deriving `{minX:p.x, minY:p.y, maxX:p.x, maxY:p.y}` a
 * second time (one truth; avoids a jscpd-flagged clone of a trivial-looking
 * but semantically load-bearing box literal).
 */
export function pointBox(p: { x: number; y: number }): Aabb {
  return { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y };
}

function unionAabb(a: Aabb | null, b: Aabb | null): Aabb | null {
  if (!a) return b;
  if (!b) return a;
  // Reuse the one-truth `boundsFromPoints` accumulation (a min/max object
  // literal here would be a third independent copy of cad-core's `unionBounds`,
  // jscpd-flagged). `a`/`b` are always finite, so the result is never null;
  // the `?? a` only satisfies the `Aabb | null` return type.
  return (
    boundsFromPoints([
      { x: a.minX, y: a.minY },
      { x: a.maxX, y: a.maxY },
      { x: b.minX, y: b.minY },
      { x: b.maxX, y: b.maxY },
    ]) ?? a
  );
}

/** Transform an AABB's four corners by `matrix` and re-AABB (via the one-truth
 *  `boundsFromPoints`) — mirrors the SVG `<g transform>` wrapping the renderer
 *  applies to a nested subtree. */
function transformAabb(box: Aabb, matrix: BlockMatrix): Aabb {
  const corners = [
    { x: box.minX, y: box.minY },
    { x: box.maxX, y: box.minY },
    { x: box.maxX, y: box.maxY },
    { x: box.minX, y: box.maxY },
  ].map((c) => matrix.transformPoint(c));
  return boundsFromPoints(corners) ?? box;
}

/**
 * Local (untransformed) bounds of ONE preview-renderable LEAF child, matched
 * to the SVG element `renderBlockChildEntity` emits — NOT the full-fidelity
 * cad-core footprint (codex PR #472 round-3 finding 4, round-4 findings 1 + 2 +
 * the self-audit Attdef sibling). Cross-checked kind-by-kind against every case
 * `renderBlockChildEntity` can draw — see the PR-body "renderer ↔ preview-bounds
 * parity" table:
 *
 *   - Text → the insertion POINT only. The renderer anchors a plain `<text>`
 *     AT `child.insertionPoint` (rotating about that same point) and NEVER reads
 *     `child.alignmentPoint`; cad-core's TEXT bounds union BOTH the insertion AND
 *     alignment points, so a far-off alignment point would inflate the viewBox
 *     with empty space the preview never fills (round-4 finding 1) — same
 *     treatment as MText.
 *   - MText → the insertion POINT only. The renderer draws a plain `<text>`
 *     anchored there with NO reference/horizontal-width, vertical-height, or
 *     rotation box (cad-core's `mtextBounds` computes that full box, which
 *     would frame empty space the preview never fills).
 *   - Attdef → the insertion POINT only. `renderAttribute` anchors its `<text>`
 *     AT `child.insertionPoint` (its per-scale annotative override substitutes
 *     `annoCtx.insertion`, NEVER `child.alignmentPoint`; the preview pane renders
 *     with `scaleName=null`, so the master insertion point is always used) — the
 *     SAME alignment-point mismatch as Text, surfaced by the self-audit sweep of
 *     every renderable kind (ATTDEF has no top-level `DrawingEntity` counterpart
 *     to dispatch through `getEntityBounds`, so it is framed directly).
 *   - Arc → the DRAWN path. The renderer emits Arc via `describeArc` →
 *     `polarPoint` (`renderHelpers.ts`), which places the point for DXF angle θ
 *     at cad-angle (θ − 90) — `polarPoint` bakes a −90° phase into cos/sin BEFORE
 *     the dialog's outer `scale(1,−1)`. So the drawn path's bounds equal
 *     cad-core's exact CAD-frame arc bounds of an arc PHASE-SHIFTED by −90° (same
 *     center/radius, identical sweep). Reuse the one-truth `getEntityBounds` via
 *     a shifted synthetic Arc — the raw-angle cad-core bounds would frame the
 *     WRONG Y half and crop an asymmetric sweep (round-4 finding 2).
 *   - LWPOLYLINE → the VERTEX box only. The renderer connects vertices with a
 *     straight `<polyline>`/`<polygon>` (no bulge arcs), so a bulge that bows
 *     outside the vertex hull (which cad-core includes) is not drawn here.
 *   - Line / Circle → the renderer draws these faithfully in RAW cad coords
 *     (`<line>`/`<circle>` at the entity's own points); reuse cad-core's exact
 *     per-kind leaf math (one truth), which matches (a Circle is symmetric, so
 *     the −90° arc-phase concern does not apply).
 */
export function previewLeafLocalBounds(child: BlockChild): Aabb | null {
  if (child.type === "Text" || child.type === "MText" || child.type === "Attdef") {
    return pointBox(child.insertionPoint);
  }
  if (child.type === "LWPOLYLINE") {
    return boundsFromPoints(child.vertices);
  }
  if (child.type === "Arc") {
    // Bounds of the path `describeArc` DRAWS: cad-core's exact arc-extrema math
    // (endpoints + every crossed cardinal) reused via a −90°-phase-shifted
    // synthetic Arc, so the frame tracks the renderer's Y-negated arc rather
    // than the CAD-frame arc the raw start/end angles would produce.
    const shifted = {
      ...child,
      startAngle: child.startAngle - 90,
      endAngle: child.endAngle - 90,
    } as unknown as DrawingEntity;
    const bounds = getEntityBounds(shifted);
    return bounds
      ? { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY }
      : null;
  }
  const bounds = getEntityBounds(child as DrawingEntity);
  return bounds
    ? { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY }
    : null;
}

function accumulatePreviewLocalBounds(
  entities: readonly BlockChild[],
  blockDefinitions: Record<string, BlockDefinition>,
  remainingDepth: number,
  visitedBlocks: ReadonlySet<string>,
): Aabb | null {
  let acc: Aabb | null = null;
  for (const child of entities) {
    if (!PREVIEW_RENDERABLE_KINDS.has(child.type)) continue;
    if (child.type === "Block") {
      const res = resolveNestedBlockChild(
        child,
        blockDefinitions,
        remainingDepth,
        visitedBlocks,
      );
      if (res.kind !== "resolved") {
        // depth-cap / cycle / missing — the renderer still draws a marker
        // glyph AT this child's insertion point, so frame that point (never
        // crop it, or a far-off marker collapses the whole viewBox).
        acc = unionAabb(acc, pointBox(child.insertionPoint));
        continue;
      }
      const nested = accumulatePreviewLocalBounds(
        res.evaluatedEntities,
        blockDefinitions,
        remainingDepth - 1,
        res.nextVisited,
      );
      // A resolved nested block with no renderable geometry emits an empty
      // `<g>` (no visible marks) — contribute nothing, matching the renderer.
      if (nested) acc = unionAabb(acc, transformAabb(nested, res.matrix));
      continue;
    }
    acc = unionAabb(acc, previewLeafLocalBounds(child));
  }
  return acc;
}

/**
 * Bounds of a block DEFINITION's child geometry AS THE SVG PREVIEW RENDERER
 * DRAWS IT — the frame for `InsertBlockDialog`'s "Образец" pane viewBox.
 * Walks the identical recursion `renderBlockInstance` → `renderBlockChildEntity`
 * walks (per-instance dynamic evaluation, `composeBlockMatrix` transforms,
 * marker insertion points, renderer-matched leaf bounds), so the viewBox
 * always frames exactly what the pane draws. Returns `null` when nothing
 * renderable resolves (caller substitutes the default box).
 */
export function computeRenderedPreviewBounds(
  blockDef: BlockDefinition,
  blockDefinitions: Readonly<Record<string, BlockDefinition>>,
): Aabb | null {
  const table: Record<string, BlockDefinition> = {
    ...blockDefinitions,
    [blockDef.name]: blockDef,
  };
  // Top-level evaluation mirrors `renderBlockInstance`'s `getCachedEvaluation`
  // (static blocks pass through by reference; dynamic blocks evaluate with the
  // preview instance's — empty — overrides, i.e. their default state).
  const topEval = evaluateBlock({
    blockDef,
    instance: { customProperties: {} },
    blockDefinitions: table,
    parentChain: [],
  });
  const visited = new Set<string>([blockDef.name]);
  const local = accumulatePreviewLocalBounds(
    topEval.entities as unknown as readonly BlockChild[],
    table,
    PREVIEW_MAX_DEPTH,
    visited,
  );
  if (!local) return null;
  // The TOP-level block's OWN base point IS subtracted — matches
  // `renderBlockInstance`'s `baseMatrix = composeBlockMatrix({insertion:(0,0),
  // scale:1, rotation:0, basePoint})` (only NESTED children's base points are
  // folded into their own `composeBlockMatrix` inside the walk above).
  const baseMatrix = composeBlockMatrix({
    translate: { x: 0, y: 0 },
    rotateDeg: 0,
    scaleX: 1,
    scaleY: 1,
    basePoint: blockDef.basePoint,
  });
  return transformAabb(local, baseMatrix);
}

/* ── P7 overlap footprint: per-instance dynamic specialization ───────────── */

/**
 * A block tree rewritten so every nested INSERT reference resolves to its OWN
 * per-instance evaluated definition. `rootName` is the synthetic name the
 * (top-level) INSERT must reference; `definitions` is a self-contained table
 * every synthetic name resolves within.
 */
export interface SpecializedBlockTree {
  readonly rootName: string;
  readonly definitions: Record<string, BlockDefinition>;
}

/** Minimal Point-at-origin definition standing in for an unresolved
 *  (missing / cyclic) nested reference, so cad-core resolves the nested
 *  INSERT to its insertion POINT — where the renderer draws the `?name` /
 *  `↺name` marker (codex PR #472 round-3 finding 3). */
function makeMarkerDefinition(name: string): BlockDefinition {
  return {
    name,
    handle: "",
    layer: "0",
    flags: 0,
    basePoint: { x: 0, y: 0, z: 0 },
    xrefPathName: "",
    description: "",
    entities: [
      {
        type: "Point",
        id: `${name}-marker`,
        spaceId: "block",
        layer: "0",
        location: { x: 0, y: 0, z: 0 },
      },
    ] as unknown as BlockDefinition["entities"],
    xrefStatus: "resolved",
  } as unknown as BlockDefinition;
}

function specializeDefinition(
  originalName: string,
  pathId: string,
  instance: {
    customProperties?: Record<string, string | number | boolean>;
    visibilityState?: string;
  },
  blockDefinitions: Record<string, BlockDefinition>,
  parentChain: readonly string[],
  out: Record<string, BlockDefinition>,
  dimStylesByName?: ReadonlyMap<string, DimensionStyle>,
): string | null {
  const def = blockDefinitions[originalName];
  if (!def) return null; // missing
  if (parentChain.includes(originalName)) return null; // cycle

  const evaluation = evaluateBlock({
    blockDef: def,
    instance: {
      customProperties: instance.customProperties ?? {},
      ...(instance.visibilityState !== undefined
        ? { visibilityState: instance.visibilityState }
        : {}),
    },
    blockDefinitions,
    parentChain,
  });
  const evaluatedEntities =
    evaluation.entities as unknown as readonly BlockChild[];

  const nextChain = [...parentChain, originalName];
  const rewritten: BlockChild[] = [];
  let childIdx = 0;
  for (const child of evaluatedEntities) {
    if (child.type === "Block") {
      const childPathId = `${pathId}.${childIdx}`;
      const childSyntheticName = specializeDefinition(
        child.blockName,
        childPathId,
        {
          customProperties: child.customProperties,
          visibilityState: child.visibilityState,
        },
        blockDefinitions,
        nextChain,
        out,
        dimStylesByName,
      );
      if (childSyntheticName) {
        rewritten.push({ ...child, blockName: childSyntheticName });
      } else {
        // Unresolved (missing / cyclic) — keep the INSERT's own placement but
        // point it at a marker definition so the marker's insertion point is
        // still framed (finding 3).
        const markerName = `${childPathId}!marker`;
        out[markerName] = makeMarkerDefinition(markerName);
        rewritten.push({ ...child, blockName: markerName });
      }
    } else if (child.type === "Multileader" && child.contentType === 1) {
      // A MLEADER with BLOCK content (group 170 contentType = 1) references
      // ANOTHER block by NAME (`child.blockName`, DXF group 343) that is NOT a
      // nested `Block` INSERT child — so the loop above never specializes it.
      // cad-core's `multileaderBlockContentBounds` resolves that content block
      // BY NAME from the SAME `definitions` table the overlap footprint hands
      // it, so unless the referenced definition is specialized INTO `out`, it
      // is treated as MISSING and the MLEADER's block-content geometry is
      // silently dropped from the footprint — an insertion overlapping ONLY the
      // MLEADER content then passes with no warning (codex PR #472 round-4
      // finding 3). Specialize the content block exactly as a nested Block child
      // (empty instance — MLEADER block content carries no DYN-1 override state
      // in the typed model) and re-point the MLEADER at the synthetic name.
      const contentPathId = `${pathId}.${childIdx}`;
      const contentSyntheticName = specializeDefinition(
        child.blockName,
        contentPathId,
        {},
        blockDefinitions,
        nextChain,
        out,
        dimStylesByName,
      );
      if (contentSyntheticName) {
        rewritten.push({ ...child, blockName: contentSyntheticName });
      } else {
        // Missing / cyclic content block — keep the MLEADER verbatim. Its
        // original `blockName` is absent from `out`, so
        // `multileaderBlockContentBounds` returns `null` and the leader/anchor
        // bounds (which already frame `textLocation` via `multileaderBounds`)
        // stand — matching the renderer's missing-content behaviour. No
        // insertion-point marker is needed (unlike a nested INSERT, whose only
        // frame IS its insertion point).
        rewritten.push(child);
      }
    } else {
      // codex PR #472 round-5 finding 1 — a DIMENSION child nested inside this
      // block resolves its referenced DIMSTYLE onto `styleOverrides` HERE (the
      // single-truth `applyDimensionStyleOverrides`, the same one the top-level
      // `resolveDimensionStyleBounds` uses), so cad-core's `dimensionBounds`
      // frames the real style-driven DIMEXE/DIMEXO/DIMGAP/DIMTXT padding
      // instead of its hardcoded defaults. Non-DIMENSION children (and the
      // no-`dimStylesByName` caller) pass through BY REFERENCE.
      rewritten.push(
        dimStylesByName
          ? applyDimensionStyleOverrides(child, dimStylesByName)
          : child,
      );
    }
    childIdx++;
  }

  const syntheticName = `${pathId}#${originalName}`;
  out[syntheticName] = {
    ...def,
    name: syntheticName,
    entities: rewritten as unknown as BlockDefinition["entities"],
  };
  return syntheticName;
}

/**
 * Rewrite `rootName`'s block tree into a per-instance-specialized table so a
 * cad-core bounds/overlap resolver (which keys geometry by NAME) still sees
 * each nested INSERT's OWN evaluated state — every reference gets a unique
 * synthetic name keyed by its recursion PATH (codex PR #472 round-3 findings
 * 2 + 5), with insertion-point markers for unresolved references (finding 3).
 * MLEADER block-content references (contentType 1) are specialized into the
 * table alongside nested INSERTs so their content geometry survives the
 * name-keyed cad-core resolver (round-4 finding 3). `dimStylesByName`
 * (optional) resolves every DIMENSION descendant's referenced DIMSTYLE onto its
 * `styleOverrides` during the rewrite, so a dimension nested inside the block
 * frames the same style-driven padding cad-core would otherwise miss (round-5
 * finding 1). Returns `null` when the root itself is missing.
 */
export function specializeBlockInstance(
  rootName: string,
  rootInstance: {
    customProperties?: Record<string, string | number | boolean>;
    visibilityState?: string;
  },
  blockDefinitions: Record<string, BlockDefinition>,
  dimStylesByName?: ReadonlyMap<string, DimensionStyle>,
): SpecializedBlockTree | null {
  const out: Record<string, BlockDefinition> = {};
  const specializedRoot = specializeDefinition(
    rootName,
    "S",
    rootInstance,
    blockDefinitions,
    [],
    out,
    dimStylesByName,
  );
  if (!specializedRoot) return null;
  return { rootName: specializedRoot, definitions: out };
}
